import { z } from 'zod';
/**
 * Контракт Media Gateway delivery layer (`/media/...`).
 *
 * Правила, которые здесь фиксируются:
 * - публичный путь потока всегда наш: `/media/<sessionId>/<resource>`;
 * - upstream origin, порт, handle TorrServer и redirect на них наружу не
 *   выдаются никогда — клиент знает только `sessionId` своей сессии;
 * - набор ресурсов закрытый (TORRSERVER_INTEGRATION.md «Playback API»):
 *   произвольный путь в gateway не принимается, иначе маршрут превращается в
 *   path traversal и open proxy.
 */
export const MEDIA_PATH_PREFIX = '/media';
/**
 * Что именно запрашивает клиент внутри сессии.
 *
 * `stream` — прямая байтовая отдача файла (direct byte-range режим),
 * остальные — HLS/DASH-раздача и дорожки. Вид ресурса определяет сервер по
 * пути, а не клиент по параметру: так набор допустимых путей остаётся конечным.
 */
export const mediaResourceKindSchema = z.enum([
    'stream',
    'manifest',
    'segment',
    'subtitle',
    'track',
]);
/**
 * Сегмент пути внутри ресурса. Первый символ обязан быть буквенно-цифровым:
 * это отсекает `.`, `..` и скрытые файлы, поэтому подняться выше по дереву
 * нельзя даже после декодирования URL.
 */
const MEDIA_PATH_SEGMENT = /^[A-Za-z0-9][A-Za-z0-9._-]{0,79}$/;
/** Глубина пути ограничена: у HLS/DASH это `<вид>/<вариант>/<файл>`. */
const MAX_PATH_SEGMENTS = 3;
export const mediaResourcePathSchema = z
    .string()
    .min(1)
    .max(240)
    .refine((value) => {
    const segments = value.split('/');
    return segments.length <= MAX_PATH_SEGMENTS && segments.every((s) => MEDIA_PATH_SEGMENT.test(s));
}, 'invalid media resource path');
/**
 * Разобранный ресурс. `path` — относительный путь внутри вида, уже
 * проверенный: `segment/0001.ts` даёт `{ kind: 'segment', path: '0001.ts' }`.
 */
export const mediaResourceSchema = z.object({
    kind: mediaResourceKindSchema,
    path: mediaResourcePathSchema,
});
/** Манифесты именуются фиксированно: имя не приходит от клиента как есть. */
const MANIFEST_NAMES = new Set(['manifest.m3u8', 'manifest.mpd']);
/** Виды, у которых путь состоит из `<вид>/<остаток>`. */
const PREFIXED_KINDS = new Set(['stream', 'segment', 'subtitle', 'track']);
/**
 * Разбирает хвост `/media/<sessionId>/` в типизированный ресурс.
 * `null` — путь не принадлежит контракту; вызывающий обязан ответить
 * ошибкой валидации, а не пытаться «угадать» ресурс.
 */
export function parseMediaResource(rest) {
    const trimmed = rest.startsWith('/') ? rest.slice(1) : rest;
    if (!mediaResourcePathSchema.safeParse(trimmed).success)
        return null;
    if (MANIFEST_NAMES.has(trimmed))
        return { kind: 'manifest', path: trimmed };
    const separator = trimmed.indexOf('/');
    if (separator <= 0)
        return null;
    const kind = mediaResourceKindSchema.safeParse(trimmed.slice(0, separator));
    if (!kind.success || !PREFIXED_KINDS.has(kind.data))
        return null;
    const path = trimmed.slice(separator + 1);
    return mediaResourcePathSchema.safeParse(path).success ? { kind: kind.data, path } : null;
}
//# sourceMappingURL=media.js.map